import React from 'react';
import { FormattedMessage } from 'react-intl';
import { push, replace } from 'react-router-redux';
import { call, put, putResolve, select } from 'redux-saga/effects';

import * as PaymentsActions from '../../actions';
import * as PaymentsConstants from '../../constants';
import * as D from '../../deps';
import * as PaymentsSelectors from '../../selectors';
import * as PaymentsServices from '../../services';
import * as PaymentsUtils from '../../utils';

const log = D.Logger.child({ component: 'src/domains/payments/sagas/workers/helpers' }, true);

export function* doesDiscountTypeRequireVerification(discountType) {
  const isDiscount = yield call(PaymentsUtils.isDiscount, discountType);
  if (!isDiscount) {
    return false;
  }

  const isDiscountVerificationInfoLoaded = yield select(
    PaymentsSelectors.isDiscountVerificationInfoLoaded,
  );
  const loadedDiscountVerificationDiscountType = yield select(
    PaymentsSelectors.getDiscountVerificationDiscountType,
  );

  if (
    !isDiscountVerificationInfoLoaded ||
    loadedDiscountVerificationDiscountType !== discountType
  ) {
    yield call(PaymentsServices.getDiscountVerificationInfo, { discountType });
  }

  const isDiscountVerificationStatusVerified = yield select(
    PaymentsSelectors.isDiscountVerificationStatusVerified,
  );
  return !isDiscountVerificationStatusVerified;
}

export function* getIsERequestIdVerified(eRequestId) {
  yield call(PaymentsServices.getDiscountVerificationInfo, { eRequestId });
  return yield select(PaymentsSelectors.isDiscountVerificationStatusVerified);
}

export function* handleGenericSubscriptionFailureWithMessage({
  errorString,
  redirectPath,
  message,
}) {
  log.error(errorString);
  const listenerMessage = message || PaymentsConstants.Messages.somethingHasGoneWrong;
  yield put(
    D.Actions.UseCases.openErrorToast({
      ...listenerMessage,
      messageName: D.Constants.Analytics.UserMessages.GENERIC_SUBSCRIPTION_FAILURE,
    }),
  );
  if (redirectPath) {
    yield put(push(redirectPath));
  }
}

export function* getDefaultDurationType(tier, discountType, paymentProvider) {
  const hasOffer = yield select(PaymentsSelectors.createHasOfferSelector);

  return PaymentsConstants.DurationTypeOrder.find(durationType => {
    return hasOffer({
      [PaymentsConstants.ProductKeys.PRODUCT_TIER]: tier,
      [PaymentsConstants.ProductKeys.DISCOUNT_TYPE]: discountType,
      [PaymentsConstants.ProductKeys.DURATION_TYPE]: durationType,
      [PaymentsConstants.ProductKeys.PAYMENT_PROVIDER]: paymentProvider,
    });
  });
}

export function* getAcquireOfferProps({
  tier,
  discountType,
  durationType: durType,
  paymentProvider,
  specialOfferCode = '',
  getSpecialOfferType: specOffType,
  paypalPurchaseInfo,
  vendorSku: venSku,
  backingProductIdentifier: backProdId,
}) {
  const durationType = durType
    ? durType
    : yield call(getDefaultDurationType, tier, discountType, paymentProvider);

  let vendorSku = venSku;
  if (!vendorSku) {
    const getOfferVendorSkuSelector = yield select(
      PaymentsSelectors.createGetOfferVendorSkuSelector,
    );
    vendorSku = yield call(getOfferVendorSkuSelector, tier, discountType, paymentProvider);
  }

  let backingProductIdentifier = backProdId;
  if (!backingProductIdentifier) {
    const getOfferBackingProductId = yield select(
      PaymentsSelectors.createGetOfferBackingProductIdSelector,
    );
    backingProductIdentifier = yield call(
      getOfferBackingProductId,
      tier,
      discountType,
      durationType,
    );
  }

  let getSpecialOfferType;
  if (specOffType) {
    getSpecialOfferType = specOffType;
  } else if (specialOfferCode) {
    getSpecialOfferType = PaymentsConstants.OfferTypes.CODE_PRODUCT;
  }

  let specialOfferToken;
  if (specialOfferCode) {
    specialOfferToken = specialOfferCode;
  }

  const tracking = JSON.stringify(yield select(PaymentsSelectors.getUpgradeOfferStatsFields));

  const props = {
    specialOfferToken,
    getSpecialOfferType,
    tierName: tier,
    discountType,
    durationType,
    vendorSku,
    backingProductIdentifier,
    paymentProvider,
    paypalPurchaseInfo,
    tracking: btoa(tracking),
    previousTier: yield select(PaymentsSelectors.getCurrentProductTier),
    previousDiscountType: yield select(PaymentsSelectors.getCurrentProductDiscountType),
  };

  if (paymentProvider === PaymentsConstants.PaymentProviders.CREDIT_CARD) {
    const creditCardFormValues = yield select(PaymentsSelectors.getCreditCardFormValues);
    const cardCompany = PaymentsUtils.getCardType({
      value: creditCardFormValues.cardNumber,
    });

    const isListenerSubmittingNewCCInfo = !!creditCardFormValues.cardNumber;
    if (isListenerSubmittingNewCCInfo) {
      const { transactionId, payload } = yield call(PaymentsUtils.getClientProfile);

      Object.assign(props, {
        creditCardInfo: { ...creditCardFormValues, cardCompany },
        atid: transactionId,
        apld: payload,
      });
    }
  }

  return props;
}

export function* logSubscriptionWebViewEvent(viewMode) {
  yield put(
    D.Actions.UseCases.webViewModeInteraction({
      page_view: D.getInDomain('stats.constants.ViewModes.SUBSCRIPTION'),
      view_mode: viewMode,
    }),
  );
}

export function* logUpgradePageStatsEvent(action) {
  yield put(
    D.Actions.UseCases.webViewModeInteraction({
      event_name: D.getInDomain('stats.constants.Events.UPGRADE_PAGE'),
      viewMode: D.getInDomain('stats.constants.ViewModes.SETTINGS'),
      action,
    }),
  );
}

export function* openWelcomePartnerModal() {
  const { dispatch } = D.getStore();
  const isMobileDevice = yield select(D.Utils.Capabilities.isMobileDevice);
  const partnerUpgradePartnerName = yield select(PaymentsSelectors.getPartnerUpgradePartnerName);
  const partnerUpgradePlanName = yield select(PaymentsSelectors.getPartnerUpgradePlanName);
  const source = PaymentsUtils.getParsedPartnerName(partnerUpgradePartnerName);
  const page_view = PaymentsUtils.getStatsPartnerConfirmationPageView(source);
  const pageUrl = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const feature = D.Constants.Urls.deepLinkParams.GENERIC_PARTNER_WELCOME;
  const mobileLongLink = yield select(D.Utils.Route.getMobileLongLink, {
    pageUrl,
    feature,
  });
  const startListeningClick = () => {
    dispatch(
      PaymentsActions.onUpgradeCompleted({
        action: PaymentsConstants.PartnerUpgradeStatsActions.START_LISTENING,
        source,
        page_view,
      }),
    );
    if (!isMobileDevice) {
      dispatch(push(D.Utils.Route.getMyMusicPath()));
      return;
    }

    if (isMobileDevice) {
      window.location = mobileLongLink;
      return;
    }
  };

  yield put(
    PaymentsActions.onUpgradeCompleted({
      action: PaymentsConstants.PartnerUpgradeStatsActions.VIEW,
      source,
      page_view,
    }),
  );

  const modalConfig = {
    startListeningClick,
    ctaButtonShouldNotClose: isMobileDevice, // Don't close the modal over an empty page on mobile
    partner: partnerUpgradePartnerName,
    partnerUpgradePlanName,
  };
  yield put(D.Actions.Modal.openWelcomePartnerUpgradeModal(modalConfig));
}

export function* confirmListenerForPartnerUpgrade() {
  const { dispatch, getState } = D.getStore();
  const partnerUpgradePartnerName = yield select(PaymentsSelectors.getPartnerUpgradePartnerName);
  const partnerUpgradeTier = yield select(PaymentsSelectors.getPartnerUpgradeTier);
  const partnerUpgradePlanName = yield select(PaymentsSelectors.getPartnerUpgradePlanName);

  const pageView = PaymentsUtils.getStatsPartnerUpgradePageView({
    partner: partnerUpgradePartnerName,
    tier: partnerUpgradePlanName,
  });

  const source = PaymentsUtils.getParsedPartnerName(partnerUpgradePartnerName);
  const primary = partnerUpgradeTier;
  const secondary = PaymentsConstants.discountTypes.NONE;

  const modalConfig = {
    listenerEmail: yield select(D.Utils.Listener.getCurrentUsername),
    partner: partnerUpgradePartnerName,
    partnerUpgradePlanName,
    ctaCallback: () => {
      dispatch(PaymentsActions.partnerUpgradeConfirmListener());
      dispatch(
        PaymentsActions.logUpgradePageEvent({
          action: PaymentsConstants.PartnerUpgradeStatsActions.CONTINUE,
          source,
          primary,
          secondary,
          pageView,
        }),
      );
      dispatch(
        PaymentsActions.requestPartnerUpgrade({
          tier: partnerUpgradeTier,
          discountType: PaymentsConstants.discountTypes.NONE,
        }),
      );
    },
    logInDifferentUserPartnerUpgrade: () => {
      dispatch(
        PaymentsActions.logUpgradePageEvent({
          action: PaymentsConstants.PartnerUpgradeStatsActions.CHANGE_ACCOUNT,
          source,
          primary,
          secondary,
          pageView,
        }),
      );
      const partnerUpgradeRoute = PaymentsSelectors.getPartnerUpgradeUrl(getState());
      dispatch(D.Actions.UseCases.signOut(partnerUpgradeRoute));
    },
  };
  yield put(D.Actions.Modal.openPartnerUpgradeConfirmUserModal(modalConfig));
  yield put(
    PaymentsActions.logUpgradePageEvent({
      action: PaymentsConstants.PartnerUpgradeStatsActions.LANDING_VIEW,
      source,
      primary,
      secondary,
      pageView,
    }),
  );
}

export function* processAuthenticatedListenerForPartnerUpgrade() {
  const isConfirmedUser = yield select(PaymentsSelectors.isPartnerUpgradeUserConfirmed);
  if (isConfirmedUser) {
    const partnerUpgradeTier = yield select(PaymentsSelectors.getPartnerUpgradeTier);
    yield put(
      PaymentsActions.requestPartnerUpgrade({
        tier: partnerUpgradeTier,
        discountType: PaymentsConstants.discountTypes.NONE,
      }),
    );
  } else {
    yield call(confirmListenerForPartnerUpgrade);
  }
}

export function* authenticateListenerForPartnerUpgrade() {
  yield put(PaymentsActions.partnerUpgradeConfirmListener());

  const partnerUpgradeUrl = yield select(PaymentsSelectors.getPartnerUpgradeUrl);
  yield put(D.Actions.Login.setAfterLoginRedirect(partnerUpgradeUrl));

  const authenticateViaRegistrationPage = yield select(D.Utils.QueryParams.getRegister);
  if (authenticateViaRegistrationPage) {
    yield put(replace(D.Utils.Route.getRegisterPath()));
  } else {
    yield put(replace(D.Utils.Route.getLoginPath()));
  }
}

export function* handlePartnerUpgradeAcquireProductError(errorType) {
  const partnerUpgradePartnerName = yield select(PaymentsSelectors.getPartnerUpgradePartnerName);
  const partnerUpgradeTier = yield select(PaymentsSelectors.getPartnerUpgradeTier);
  const partnerUpgradePlanName = yield select(PaymentsSelectors.getPartnerUpgradePlanName);

  const isTierIneligible = errorType === PaymentsConstants.ErrorCodes.PRODUCT_INELIGIBLE;
  const isAgeIneligible = errorType === PaymentsConstants.ErrorCodes.FAMILY_PLAN_AGE_INELIGIBLE;
  const isEntitlementAlreadyActive =
    errorType === PaymentsConstants.ErrorCodes.BANGO_ENTITLEMENT_ALREADY_ACTIVE;
  if (isTierIneligible || isAgeIneligible) {
    const { dispatch } = D.getStore();
    const partnerUpgradeRoute = yield select(PaymentsSelectors.getPartnerUpgradeUrl);
    const register = D.Constants.App.StartupQueryParameterKeys.REGISTER;
    const source = PaymentsUtils.getParsedPartnerName(partnerUpgradePartnerName);
    const primary = partnerUpgradeTier;
    const secondary = PaymentsConstants.discountTypes.NONE;
    const pageView = source + PaymentsConstants.PageViewSuffixes.CONTINUE_ATTEMPT;
    const registerNewUserPartnerUpgrade = () => {
      dispatch(
        PaymentsActions.logUpgradePageEvent({
          action: PaymentsConstants.PartnerUpgradeStatsActions.NEW_ACCOUNT,
          source,
          primary,
          secondary,
          pageView,
        }),
      );
      dispatch(D.Actions.UseCases.signOut(`${partnerUpgradeRoute}&${register}=1`));
    };
    const logInDifferentUserPartnerUpgrade = () => {
      dispatch(
        PaymentsActions.logUpgradePageEvent({
          action: PaymentsConstants.PartnerUpgradeStatsActions.CHANGE_ACCOUNT,
          source,
          primary,
          secondary,
          pageView,
        }),
      );
      dispatch(D.Actions.UseCases.signOut(partnerUpgradeRoute));
    };
    const sharedModalConfig = {
      loginDifferentAccount: PaymentsConstants.Messages.loginDifferentAccount,
      createNewAccount: PaymentsConstants.Messages.createNewAccount,
      registerNewUserPartnerUpgrade,
      logInDifferentUserPartnerUpgrade,
    };
    if (isTierIneligible) {
      const modalConfig = {
        ...sharedModalConfig,
        listenerEmail: yield select(D.Utils.Listener.getCurrentUsername),
        ineligiblePartnerUpgradeMessage: PaymentsConstants.Messages.ineligiblePartnerUpgrade,
        partner: partnerUpgradePartnerName,
        partnerUpgradePlanName,
      };
      yield put(D.Actions.Modal.openPartnerUpgradeIneligibleModal(modalConfig));
    } else {
      const modalConfig = {
        ...sharedModalConfig,
        ageIneligiblePartnerUpgradeMessageBody: PaymentsConstants.Messages.underAgeFPBody,
        ageIneligiblePartnerUpgradeMessageSubBody: PaymentsConstants.Messages.underAgeFPSubBody,
      };
      yield put(D.Actions.Modal.openPartnerUpgradeAgeIneligibleModal(modalConfig));
    }
    yield put(
      PaymentsActions.logUpgradePageEvent({
        action: PaymentsConstants.PartnerUpgradeStatsActions.INELIGIBLE_LANDING_VIEW,
        source,
        primary,
        secondary,
        pageView,
      }),
    );
  } else if (isEntitlementAlreadyActive) {
    yield call(openWelcomePartnerModal);
  } else {
    log.error('Other Partner Upgrade Errors');
    const modalConfig = {
      partner: partnerUpgradePartnerName,
    };
    yield put(D.Actions.Modal.openPartnerUpgradeErrorOverlay(modalConfig));
  }

  yield put(PaymentsActions.exitPartnerUpgrade());
}

function getModalConfig() {
  const state = D.getStore().getState();
  const isMobileDevice = D.Utils.Capabilities.isMobileDevice(state);
  const pageUrl = D.Utils.Route.getCurrentPathnameFromState(state);
  let modalConfig;

  if (isMobileDevice) {
    const inviteMobileLongLink = D.Utils.Route.getMobileLongLink(state, {
      pageUrl: D.Utils.Route.getAccountBillingUniversalLinkPathname(),
      feature: D.Constants.Urls.deepLinkParams.GENERIC_PARTNER_FAMILY_WELCOME,
    });
    const invitePeopleMobileOnClick = () => {
      window.location = inviteMobileLongLink;
    };
    const skipMobileLongLink = D.Utils.Route.getMobileLongLink(state, {
      pageUrl,
      feature: D.Constants.Urls.deepLinkParams.GENERIC_PARTNER_FAMILY_WELCOME,
    });
    const skipForNowMobileOnClick = () => {
      window.location = skipMobileLongLink;
    };

    modalConfig = {
      ctaButtonFunc: invitePeopleMobileOnClick,
      closeButtonFunc: skipForNowMobileOnClick,
      ctaButtonShouldNotClose: true,
      closeButtonShouldNotClose: true,
    };
  } else {
    modalConfig = {
      ctaButtonUrl: D.Utils.Route.getFamilyPlanManagementPath(),
      closeButtonUrl: D.Utils.Route.getNowPlayingPath(),
    };
  }
  return modalConfig;
}

// TODO: Remove this from the worker https://jira.savagebeast.com/browse/WEBCLIENT-8173
function* handleOverlayMessage() {
  const isInSpecialOfferFlow = yield select(PaymentsSelectors.isInSpecialOfferFlow);
  const activeTier = yield select(PaymentsSelectors.getActiveProductGroupTier);
  const activeDiscountType = yield select(PaymentsSelectors.getActiveProductDiscountType);
  const isNonPaymentBackedUser = yield select(PaymentsSelectors.isNonPaymentBackedUser);
  let messageProps = {
    values: {
      planName: PaymentsUtils.getPlanName(activeTier, activeDiscountType),
    },
  };

  if (activeTier !== PaymentsConstants.tiers.PANDORA_PREMIUM) {
    // we don't want to show this for Premium yet (LR only)
    if (isInSpecialOfferFlow) {
      messageProps = {
        ...messageProps,
        ...PaymentsConstants.Messages.subscribedPromoNotification,
      };
    } else if (isNonPaymentBackedUser) {
      messageProps = {
        ...messageProps,
        ...PaymentsConstants.Messages.giftContinuation,
      };
    } else {
      messageProps = {
        ...messageProps,
        ...PaymentsConstants.Messages.subscribedNotification,
      };
    }
    const message = <FormattedMessage {...messageProps} />;
    yield putResolve(
      D.Actions.Overlay.openOverlay({
        [D.Constants.Overlay.OVERLAY_CONFIG.TYPE]: D.Constants.Overlay.OVERLAY_TYPES.blueBar,
        [D.Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]:
          D.Constants.Overlay.SubscriptionMessageBlueBarSubtype,
        [D.Constants.Overlay.OVERLAY_CONFIG.PROPS]: {
          message,
        },
      }),
    );
  }
}

export function* processAcquireProductSuccess({
  tierName,
  discountType,
  previousTier,
  previousDiscountType,
  vendorSku,
  paymentProvider,
  specialOfferType,
}) {
  const isPartnerUpgrade = vendorSku === PaymentsConstants.PARTNER_SKU;
  const isAudioPlaying = yield select(D.getInDomain('playback.utils.isAudioPlaying', false));
  const isMobileDevice = yield select(D.Utils.Capabilities.isMobileDevice);

  yield call(D.putAndWait, D.Actions.Application.listenerReauth());

  const shouldShowPremiumFeatures = yield select(D.Utils.Listener.hasPremiumFeatures);
  const seenWebPremiumWelcome = yield select(D.Utils.Listener.hasSeenWebPremiumWelcome);

  // This should only be used by Generic Partner
  // TODO remove with WEBCLIENT-7964
  let newTier;
  if (tierName) {
    newTier = tierName;
  } else {
    newTier = yield select(PaymentsSelectors.getActiveProductGroupTier);
  }

  if (newTier === PaymentsConstants.tiers.PANDORA_FAMILY) {
    const isUpdatingDurationOnly = PaymentsUtils.isSameProduct(previousTier, null, newTier, null);

    if (isUpdatingDurationOnly) {
      if (paymentProvider === PaymentsConstants.PaymentProviders.PAYPAL) {
        yield put(push(D.Utils.Route.getSubscriptionPath()));

        const message = (
          <FormattedMessage {...PaymentsConstants.Messages.subscriptionHasBeenUpdated} />
        );
        yield putResolve(
          D.Actions.Overlay.openOverlay({
            [D.Constants.Overlay.OVERLAY_CONFIG.TYPE]: D.Constants.Overlay.OVERLAY_TYPES.blueBar,
            [D.Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]:
              D.Constants.Overlay.SubscriptionMessageBlueBarSubtype,
            [D.Constants.Overlay.OVERLAY_CONFIG.PROPS]: {
              message,
            },
          }),
        );
      }
    } else {
      const modalConfig = getModalConfig();
      if (isPartnerUpgrade) {
        const partnerName = yield select(PaymentsSelectors.getPartnerUpgradePartnerName);
        const partnerUpgradePlanName = yield select(PaymentsSelectors.getPartnerUpgradePlanName);
        const source = PaymentsUtils.getParsedPartnerName(partnerName);
        const page_view = PaymentsUtils.getStatsPartnerConfirmationPageView(source);
        modalConfig.partnerUpgradePlanName = partnerUpgradePlanName;
        modalConfig.partner = partnerName;

        yield put(
          PaymentsActions.onUpgradeCompleted({
            action: PaymentsConstants.PartnerUpgradeStatsActions.VIEW,
            source,
            page_view,
          }),
        );
      }

      yield put(D.Actions.Modal.openWelcomeParentToPremiumFamilyModal(modalConfig));

      if (isPartnerUpgrade) {
        yield put(PaymentsActions.exitPartnerUpgrade()); // has to be called after parent modal is opened
      }
    }

    return;
  }

  if (isPartnerUpgrade) {
    yield call(openWelcomePartnerModal);
    yield put(PaymentsActions.exitPartnerUpgrade());
  }

  if (paymentProvider === PaymentsConstants.PaymentProviders.PAYPAL) {
    yield put(PaymentsActions.resetPaypalStatus());
  } else if (paymentProvider === PaymentsConstants.PaymentProviders.CREDIT_CARD) {
    yield put(PaymentsActions.resetCreditCardForm());
  }

  if (specialOfferType === PaymentsConstants.OfferTypes.CODE_PRODUCT) {
    yield put(push(D.Utils.Route.getCongratulationsPath()));
    return;
  }

  if (isMobileDevice) {
    yield put(replace(D.Utils.Route.getSubscriptionWelcomePath()));
    return;
  }

  if (newTier === PaymentsConstants.tiers.PANDORA_PREMIUM) {
    if (shouldShowPremiumFeatures && !seenWebPremiumWelcome) {
      yield put(push(D.Utils.Route.getMyMusicAllFilterPath()));
    } else {
      yield put(push(D.Utils.Route.getSubscriptionPath()));
    }
  } else if (isAudioPlaying) {
    yield put(push(D.Utils.Route.getNowPlayingPath()));
  } else if (newTier === PaymentsConstants.tiers.PANDORA_PLUS) {
    yield put(push(D.Utils.Route.getMyStationsPath()));
  }

  yield call(handleOverlayMessage);
}

export function* shouldRedirectFromUpgradeToSubscription(upgradeTier, upgradeDiscountType) {
  return PaymentsUtils.shouldRedirectFromUpgradeToSubscription(
    yield select(PaymentsSelectors.getActiveProductGroupTier),
    yield select(PaymentsSelectors.getActiveProductDiscountType),
    upgradeTier,
    upgradeDiscountType,
    yield select(PaymentsSelectors.isNonPaymentBackedUser),
    yield select(PaymentsSelectors.isListenerComplimentary),
    yield call(doesDiscountTypeRequireVerification, upgradeDiscountType),
  );
}

export function* logUpgradeResult({ action, page_view, view_mode }) {
  const eventData = {
    action,
    page_view,
    view_mode,
  };
  yield put(PaymentsActions.onUpgradeCompleted(eventData));
  yield put(D.Actions.UseCases.webViewModeInteraction(eventData));
}

export function* logUpgradePage({ action, page_view, view_mode }) {
  yield put(
    PaymentsActions.logUpgradePageEvent({
      action,
      pageView: page_view,
      viewMode: view_mode,
    }),
  );
  yield put(
    D.Actions.UseCases.webViewModeInteraction({
      action,
      page_view,
      view_mode,
    }),
  );
}

export function* logSubscriptionResults({ suffix, specialOfferCode }) {
  let page_view = PaymentsConstants.Analytics.SUBSCRIPTION + suffix;
  if (yield select(PaymentsSelectors.isPartnerUpgradeFlow)) {
    page_view = PaymentsConstants.Analytics.PARTNER_SUBSCRIPTION + suffix;
  } else if (specialOfferCode) {
    page_view = PaymentsConstants.Analytics.OFFER_REDEEM_SUBSCRIPTION + suffix;
  }

  yield call(logUpgradeResult, {
    action: D.getInDomain('stats.constants.StatsActions.VIEW'),
    page_view,
    view_mode: D.getInDomain('stats.constants.ViewModes.STANDARD'),
  });
}

export function* completePaypal() {
  const paypalToken = yield select(D.Utils.QueryParams.getToken);
  const paypalPurchaseInfo = {
    paypalToken,
  };
  const tier = yield select(PaymentsSelectors.getPaypalTier);
  const discountType = yield select(PaymentsSelectors.getPaypalDiscountType);
  const durationType = yield select(PaymentsSelectors.getPaypalDurationType);
  const paymentProvider = PaymentsConstants.PaymentProviders.PAYPAL;
  const specialOfferCode = yield select(PaymentsSelectors.getPaypalSpecialOfferCode);
  const vendorSku = yield select(PaymentsSelectors.getPaypalVendorSku);
  const backingProductIdentifier = yield select(
    PaymentsSelectors.getPaypalBackingProductIdentifier,
  );
  const paypalData = {
    paypalPurchaseInfo,
    tier,
    discountType,
    durationType,
    paymentProvider,
    specialOfferCode,
    vendorSku,
    backingProductIdentifier,
  };

  if (paypalToken && vendorSku && backingProductIdentifier) {
    log.info(`completePaypal(): completing paypal flow ${JSON.stringify(paypalData)}`);
    try {
      yield D.putAndWait(PaymentsActions.acquireOffer(paypalData));
    } catch (error) {
      log.error(error, 'completePaypal(): acquireOffer failed');
      yield call(processCompletePaypalError);
    }
  } else {
    log.error(
      `completePaypal(): paypalToken, vendorSku, and backingProductIdentifier are required: ${JSON.stringify(
        { paypalToken, ...paypalData },
      )}`,
    );
    yield put(
      D.Actions.UseCases.openErrorToast(
        PaymentsConstants.Messages.somethingHasGoneWrongRefreshRetry,
      ),
    );
    yield call(processCompletePaypalError);
  }
}

export function* processCompletePaypalError() {
  yield put(PaymentsActions.setPaypalComplete());
  yield put(push(D.Utils.Route.getUpgradePath()));
}
